import React, { type FC, useMemo } from "react";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { BusinessForm } from "@src/components/business-form";
import { DetailList } from "@shared/ui/detail-row";
import { EmptyState } from "@shared/ui/empty-state";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel } from "@shared/ui/form-panel";
import { Input } from "@shared/ui/input";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import type { ActOfCheckingAvailabilityContent } from "../../../../../../api/actOfCheckingAvailability";
import type { StorePlaceRemainder } from "../../../../../../api/storePlaces";
import {
    formatInventoryCells,
    isStockPartialInventoryContentInventoried,
    isStockPartialInventoryContentLotBased,
} from "../../helpers/formatStockPartialInventoryContent";
import type { UseStockPartialInventoryCardMutationsResult } from "../../useStockPartialInventoryCardMutations";
import {
    stockPartialInventoryContentFormConfig,
    type StockPartialInventoryContentFormValues,
} from "./stockPartialInventoryContent.form-config";
import "./StockPartialInventoryContentForm.scss";

/** Пропсы компонента {@link StockPartialInventoryContentForm}. */
export interface StockPartialInventoryContentFormProps {
    /** Код строки состава АПН. */
    contentCode: number;
    /** Строки текущего АПН. */
    contents: ActOfCheckingAvailabilityContent[];
    /** Мутация обновления фактических количеств. */
    updateFactQtiesMutation: UseStockPartialInventoryCardMutationsResult["updateFactQtiesMutation"];
}

/** Форма инвентаризации строки АПН. */
export const StockPartialInventoryContentForm: FC<StockPartialInventoryContentFormProps> = ({
    contentCode,
    contents,
    updateFactQtiesMutation,
}) => {
    const content = contents.find((item) => item.Code === contentCode);

    if (content == null) {
        return <EmptyState text="Строка АПН не найдена" />;
    }

    if (!isStockPartialInventoryContentLotBased(content)) {
        return <EmptyState text="Инвентаризация без партии пока не поддерживается" />;
    }

    return <StockPartialInventoryContentFormBody content={content} updateFactQtiesMutation={updateFactQtiesMutation} />;
};

interface StockPartialInventoryContentFormBodyProps {
    content: ActOfCheckingAvailabilityContent;
    updateFactQtiesMutation: UseStockPartialInventoryCardMutationsResult["updateFactQtiesMutation"];
}

const StockPartialInventoryContentFormBody: FC<StockPartialInventoryContentFormBodyProps> = ({
    content,
    updateFactQtiesMutation,
}) => {
    const cellRemainders = useMemo(() => resolveCellRemainders(content), [content]);
    const isInventoried = isStockPartialInventoryContentInventoried(content);
    const unitDecorator = measureEndDecorator(content.Measure);
    const initialValues = useMemo(
        (): StockPartialInventoryContentFormValues => buildInitialValues(content, cellRemainders, isInventoried),
        [cellRemainders, content, isInventoried],
    );

    const handleSubmit = async (values: StockPartialInventoryContentFormValues): Promise<void> => {
        const domainValues = stockPartialInventoryContentFormConfig.toDomain(values);
        const factQty = domainValues.factQty;

        if (factQty == null) {
            return;
        }

        await updateFactQtiesMutation.mutateAsync({
            contentCode: content.Code,
            factQty,
            factQtyByStorePlaces: domainValues.factQtyByStorePlaces,
        });
    };

    return (
        <FormPanel<StockPartialInventoryContentFormValues>
            useDefaultFooter={false}
            formProps={{
                formConfig: stockPartialInventoryContentFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={({ submitForm, isFormSubmitting }) => (
                <FormFooter confirmText="Сохранить" confirmLoading={isFormSubmitting} onConfirm={submitForm} />
            )}
        >
            {(form) => (
                <div className="stock_partial_inventory_content_form">
                    <DetailList layout="tiles" columns="auto" minWidth={112}>
                        <DetailList.Row label="Номенклатура" value={<BusinessStockObjValue stockObj={content.StockObj} />} />
                        {isInventoried ? (
                            <DetailList.Row
                                label="Факт. кол-во"
                                value={<BusinessMeasureValue value={content.FactReminder} measure={content.Measure} />}
                            />
                        ) : null}
                        <DetailList.Row label="Партия" value={<BusinessLotValue lot={content.Lot} />} />
                        <DetailList.Row label="Ячейки хранения" value={formatInventoryCells(content.PlacedCellRemainders)} />
                        <DetailList.Row label="Партия инвентаризирована" value={isInventoried ? "Да" : "Нет"} />
                    </DetailList>
                    {cellRemainders.length === 0 ? (
                        <BusinessForm.Field name="factQty">
                            <Input
                                type="number"
                                {...form.field("factQty").getNumberInputProps()}
                                endDecorator={unitDecorator}
                            />
                        </BusinessForm.Field>
                    ) : (
                        initialValues.factQtyByStorePlaces.map((item, index) => (
                            <BusinessForm.Field key={item.spCode} name={`factQtyByStorePlaces.${index}.factQty`}>
                                <Input
                                    type="number"
                                    {...form.field(`factQtyByStorePlaces.${index}.factQty`).getNumberInputProps()}
                                    endDecorator={unitDecorator}
                                />
                            </BusinessForm.Field>
                        ))
                    )}
                </div>
            )}
        </FormPanel>
    );
};

/**
 * Стартовые значения формы: до инвентаризации поля пустые (0), \
 * после — текущий факт / разрез по ячейкам.
 */
function buildInitialValues(
    content: ActOfCheckingAvailabilityContent,
    cellRemainders: StorePlaceRemainder[],
    isInventoried: boolean,
): StockPartialInventoryContentFormValues {
    if (cellRemainders.length === 0) {
        return {
            factQty: isInventoried ? content.FactReminder : 0,
            factQtyByStorePlaces: [],
        };
    }

    return {
        factQty: isInventoried ? content.FactReminder : 0,
        factQtyByStorePlaces: cellRemainders.map((item) => ({
            spCode: item.SpCode,
            cellText: item.StorePlace?.Num ?? `Ячейка ${item.SpCode}`,
            factQty: isInventoried ? item.Qty : 0,
        })),
    };
}

function resolveCellRemainders(content: ActOfCheckingAvailabilityContent): StorePlaceRemainder[] {
    if (content.PlacedCellRemainders != null && content.PlacedCellRemainders.length > 0) {
        return content.PlacedCellRemainders;
    }

    return [];
}
